import { useEffect, useState } from "react";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Puzzle, Upload, Loader2, Save, Search, LockOpen, Lock } from "lucide-react";

function GrantPanel({ addon }) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const days = Number(addon.duration_days || 365);

  useEffect(() => {
    const needle = q.trim();
    if (needle.length < 2) {
      setHits([]);
      setSearching(false);
      return;
    }
    let alive = true;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/platform/addons/${addon.id}/tenants`, { params: { q: needle } });
        if (alive) setHits(data.items || []);
      } catch (e) {
        if (alive) toast.error(extractErr(e));
      } finally {
        if (alive) setSearching(false);
      }
    }, 280);
    return () => { alive = false; clearTimeout(t); };
  }, [q, addon.id]);

  const grant = async (t) => {
    setBusyId(t.id);
    try {
      await api.put(`/platform/addons/tenants/${t.id}/${addon.id}/grant`, { days });
      toast.success(`${addon.name} unlocked for ${t.name || t.slug} (${days} days)`);
      setHits((xs) => xs.map((x) => (x.id === t.id ? { ...x, entitlement: { ...x.entitlement, active: true, status: "active" } } : x)));
      const { data } = await api.get(`/platform/addons/${addon.id}/tenants`, { params: { q: q.trim() } });
      setHits(data.items || []);
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setBusyId(null);
    }
  };

  const revoke = async (t) => {
    setBusyId(t.id);
    try {
      await api.put(`/platform/addons/tenants/${t.id}/${addon.id}/revoke`);
      toast.success(`${addon.name} revoked for ${t.name || t.slug}`);
      const { data } = await api.get(`/platform/addons/${addon.id}/tenants`, { params: { q: q.trim() } });
      setHits(data.items || []);
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="rounded-xl border border-violet-900/50 bg-slate-950/60 p-4 space-y-3" data-testid={`saddon-grant-${addon.id}`}>
      <div>
        <div className="text-sm font-semibold text-slate-100">Grant access</div>
        <p className="text-xs text-slate-400 mt-0.5">Search a store and unlock this addon for 1 year (offline). Merchants can also buy it from App Store.</p>
      </div>
      <div className="relative">
        <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search tenant name, slug, or email…"
          className="pl-9 bg-slate-900 border-slate-700"
          data-testid={`saddon-search-${addon.id}`}
        />
      </div>
      {searching && <div className="text-xs text-slate-500 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…</div>}
      {!searching && q.trim().length >= 2 && hits.length === 0 && (
        <div className="text-xs text-slate-500">No matching tenants.</div>
      )}
      {hits.length > 0 && (
        <div className="space-y-2">
          {hits.map((t) => {
            const active = Boolean(t.entitlement?.active);
            const exp = t.entitlement?.expires_at;
            return (
              <div key={t.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-800 bg-slate-900 px-3 py-2" data-testid={`saddon-hit-${addon.id}-${t.slug}`}>
                <div className="min-w-0">
                  <div className="text-sm text-slate-100 truncate">{t.name}</div>
                  <div className="text-xs text-slate-500 font-mono-nums">{t.slug} · {t.status}</div>
                  {active && exp && (
                    <div className="text-xs text-emerald-400 mt-0.5">Active until {new Date(exp).toLocaleDateString("en-IN")}</div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {active
                    ? <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">Active</Badge>
                    : <Badge className="bg-slate-700/40 text-slate-300 border-slate-600">Locked</Badge>}
                  {active ? (
                    <Button size="sm" variant="outline" disabled={busyId === t.id} onClick={() => revoke(t)} className="border-orange-800 text-orange-200 hover:bg-orange-950" data-testid={`saddon-revoke-${addon.id}-${t.slug}`}>
                      {busyId === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lock className="w-3.5 h-3.5 mr-1" />} Revoke
                    </Button>
                  ) : (
                    <Button size="sm" disabled={busyId === t.id} onClick={() => grant(t)} className="bg-violet-600 hover:bg-violet-500" data-testid={`saddon-grant-btn-${addon.id}-${t.slug}`}>
                      {busyId === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LockOpen className="w-3.5 h-3.5 mr-1" />} Give 1 year
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function SuperAddons() {
  const [items, setItems] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);

  const load = () => api.get("/platform/addons").then(({ data }) => setItems(data.items || [])).catch((e) => toast.error(extractErr(e)));
  useEffect(() => { load(); }, []);

  const updateLocal = (id, patch) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const save = async (a) => {
    setSavingId(a.id);
    try {
      const features = typeof a.features === "string"
        ? a.features.split("\n").map((s) => s.trim()).filter(Boolean)
        : (a.features || []);
      const { data } = await api.put(`/platform/addons/${a.id}`, {
        name: a.name,
        description: a.description,
        features,
        image_url: a.image_url || null,
        price: Number(a.price),
        duration_days: Number(a.duration_days || 365),
        is_active: Boolean(a.is_active),
        is_featured: Boolean(a.is_featured),
        featured_rank: Number(a.featured_rank || 0),
        tagline: a.tagline || "",
        badge: a.badge || "",
        rating_avg: Number(a.rating_avg || 0),
        rating_count: Number(a.rating_count || 0),
        stores_using: Number(a.stores_using || 0),
        theme: a.theme || "slate",
      });
      updateLocal(a.id, data);
      toast.success("Addon saved");
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setSavingId(null);
    }
  };

  const upload = async (id, file) => {
    if (!file) return;
    setUploadingId(id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/platform/addons/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateLocal(id, { image_url: data.url });
      toast.success("Photo uploaded — save to keep it");
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-100 flex items-center gap-2">
          <Puzzle className="w-7 h-7 text-violet-300" /> Paid Addons
        </h1>
        <p className="text-slate-400 text-sm mt-1">Set yearly price, photo, featured placement, and star ratings. Search a tenant on each addon to grant 1-year access. Merchants see these in App Store.</p>
      </div>
      {items.map((a) => (
        <Card key={a.id} className="bg-slate-900 border-slate-800 p-6 space-y-4 text-slate-100" data-testid={`saddon-${a.id}`}>
          <div className="flex items-center justify-between">
            <div className="font-mono-nums text-xs text-slate-500">{a.id}</div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-400">Featured</span>
              <Switch checked={Boolean(a.is_featured)} onCheckedChange={(v) => updateLocal(a.id, { is_featured: v })} data-testid={`saddon-featured-${a.id}`} />
              <span className="text-slate-400 ml-2">Listed</span>
              <Switch checked={Boolean(a.is_active)} onCheckedChange={(v) => updateLocal(a.id, { is_active: v })} />
            </div>
          </div>
          <div className="flex gap-4">
            <div className="w-28 h-28 rounded-lg bg-slate-800 overflow-hidden shrink-0 border border-slate-700">
              {a.image_url ? <img src={mediaUrl(a.image_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-500 text-xs">No photo</div>}
            </div>
            <div className="flex-1 space-y-3">
              <div>
                <Label className="text-slate-300">Name</Label>
                <Input value={a.name || ""} onChange={(e) => updateLocal(a.id, { name: e.target.value })} className="mt-1 bg-slate-950 border-slate-700" />
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-indigo-300 cursor-pointer">
                <Upload className="w-4 h-4" /> {uploadingId === a.id ? "Uploading…" : "Upload photo"}
                <input type="file" hidden accept="image/*" onChange={(e) => e.target.files?.[0] && upload(a.id, e.target.files[0])} />
              </label>
            </div>
          </div>
          <div>
            <Label className="text-slate-300">Description</Label>
            <Textarea rows={3} value={a.description || ""} onChange={(e) => updateLocal(a.id, { description: e.target.value })} className="mt-1 bg-slate-950 border-slate-700" />
          </div>
          <div>
            <Label className="text-slate-300">Features (one per line)</Label>
            <Textarea rows={5} value={Array.isArray(a.features) ? a.features.join("\n") : (a.features || "")} onChange={(e) => updateLocal(a.id, { features: e.target.value })} className="mt-1 bg-slate-950 border-slate-700 font-mono text-sm" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300">Tagline</Label>
              <Input value={a.tagline || ""} onChange={(e) => updateLocal(a.id, { tagline: e.target.value })} className="mt-1 bg-slate-950 border-slate-700" data-testid={`saddon-tagline-${a.id}`} />
            </div>
            <div>
              <Label className="text-slate-300">Badge (e.g. MOST POPULAR / NEW)</Label>
              <Input value={a.badge || ""} onChange={(e) => updateLocal(a.id, { badge: e.target.value })} className="mt-1 bg-slate-950 border-slate-700" data-testid={`saddon-badge-${a.id}`} />
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-slate-300">Star rating (0–5)</Label>
              <Input type="number" step="0.1" min="0" max="5" value={a.rating_avg ?? ""} onChange={(e) => updateLocal(a.id, { rating_avg: e.target.value })} className="mt-1 bg-slate-950 border-slate-700 font-mono-nums" data-testid={`saddon-rating-avg-${a.id}`} />
            </div>
            <div>
              <Label className="text-slate-300">Review count</Label>
              <Input type="number" min="0" value={a.rating_count ?? ""} onChange={(e) => updateLocal(a.id, { rating_count: e.target.value })} className="mt-1 bg-slate-950 border-slate-700 font-mono-nums" data-testid={`saddon-rating-count-${a.id}`} />
            </div>
            <div>
              <Label className="text-slate-300">Stores using</Label>
              <Input type="number" min="0" value={a.stores_using ?? ""} onChange={(e) => updateLocal(a.id, { stores_using: e.target.value })} className="mt-1 bg-slate-950 border-slate-700 font-mono-nums" data-testid={`saddon-stores-${a.id}`} />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300">Yearly price (₹, before GST)</Label>
              <Input type="number" step="0.01" value={a.price ?? ""} onChange={(e) => updateLocal(a.id, { price: e.target.value })} className="mt-1 bg-slate-950 border-slate-700 font-mono-nums" data-testid={`saddon-price-${a.id}`} />
            </div>
            <div>
              <Label className="text-slate-300">Duration (days)</Label>
              <Input type="number" value={a.duration_days ?? 365} onChange={(e) => updateLocal(a.id, { duration_days: e.target.value })} className="mt-1 bg-slate-950 border-slate-700 font-mono-nums" />
            </div>
            <div>
              <Label className="text-slate-300">Featured rank (0 = first)</Label>
              <Input type="number" value={a.featured_rank ?? 0} onChange={(e) => updateLocal(a.id, { featured_rank: e.target.value })} className="mt-1 bg-slate-950 border-slate-700 font-mono-nums" data-testid={`saddon-featured-rank-${a.id}`} />
            </div>
            <div>
              <Label className="text-slate-300">Card theme</Label>
              <select value={a.theme || "slate"} onChange={(e) => updateLocal(a.id, { theme: e.target.value })} className="mt-1 w-full h-10 rounded-md bg-slate-950 border border-slate-700 px-3 text-sm" data-testid={`saddon-theme-${a.id}`}>
                <option value="amber">Amber / gold</option>
                <option value="sky">Sky / lavender</option>
                <option value="violet">Violet</option>
                <option value="emerald">Emerald</option>
                <option value="rose">Rose</option>
                <option value="slate">Slate</option>
              </select>
            </div>
          </div>
          <Button onClick={() => save(a)} disabled={savingId === a.id} className="bg-indigo-600 hover:bg-indigo-500" data-testid={`saddon-save-${a.id}`}>
            {savingId === a.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save
          </Button>
          <GrantPanel addon={a} />
        </Card>
      ))}
    </div>
  );
}
